<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { formatDate24h } from '@/utils/dateFormatter'
import { useToast } from '@/composables/useToast'
import { useTour } from '@/composables/useTour'
import { campaignTourSteps } from '@/composables/tours/campaignTour'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import CampaignEditDialog from '@/views/apps/campaign/CampaignEditDialog.vue'

// Lazy load DisplayFieldsConfig for better performance
const DisplayFieldsConfig = defineAsyncComponent(() => import('@/components/campaign/DisplayFieldsConfig.vue'))

// Define page meta for CASL permissions
definePage({
  meta: {
    action: 'manage',
    subject: 'Campaign',
  },
})

// 👉 Composables
const { t } = useI18n()
const router = useRouter()
const { showToast } = useToast()
const { startTour } = useTour()

// 👉 Reactive data
const saving = ref(false)

// 👉 Search and filter options
const searchQuery = ref('')
const selectedStatus = ref()
const selectedIsActive = ref()

// Status options for filters
const statusFilterOptions = computed(() => [
  { title: t('campaign.statusDraft'), value: 'draft' },
  { title: t('campaign.statusActive'), value: 'active' },
  { title: t('campaign.statusPaused'), value: 'paused' },
  { title: t('campaign.statusCompleted'), value: 'completed' },
  { title: t('campaign.statusCancelled'), value: 'cancelled' },
])

// IsActive options
const isActiveOptions = computed(() => [
  { title: t('common.active'), value: true },
  { title: t('common.inactive'), value: false },
])

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

// Update data table options
const updateOptions = (options: any) => {
  sortBy.value = options.sortBy[0]?.key
  orderBy.value = options.sortBy[0]?.order
}

// 👉 Fetching campaigns
const { data: campaignsData, execute: loadCampaigns, isFetching: loading } = useApi<any>(createUrl('/campaigns', {
  query: {
    search: searchQuery,
    status: selectedStatus,
    isActive: selectedIsActive,
    itemsPerPage,
    page,
    sortBy,
    sortOrder: orderBy,
  },
}))

const campaigns = computed(() => campaignsData.value?.data || [])
const totalItems = computed(() => campaignsData.value?.total || 0)

// 👉 Fetching wrapup templates
const { data: wrapupTemplatesData, execute: loadWrapupTemplates } = useApi<any>(createUrl('/wrapup-template'))

const wrapupTemplates = computed(() => wrapupTemplatesData.value?.data || [])

// 👉 Fetching field mapping for available fields
const { data: fieldMappingData, execute: loadFieldMapping } = useApi<any>(createUrl('/call-lists/field-mapping'))

const availableFields = computed(() => fieldMappingData.value?.fields || [])

// Dialog states
const showDialog = ref(false)
const showConfirmDialog = ref(false)
const showDisplayFieldsDialog = ref(false)
const currentCampaign = ref(null)
const savingDisplayFields = ref(false)

// Confirm dialog
const confirmDialog = ref({
  title: '',
  message: '',
  confirmText: '',
  cancelText: '',
  action: () => {},
})

// Display fields configuration
const displayFieldsConfig = ref({
  dataTable: [],
  callInfo: [],
  callLog: [],
})

// 👉 Table headers
const headers = computed(() => [
  {
    title: t('campaign.campaignName'),
    key: 'name',
    sortable: true,
  },
  {
    title: t('campaign.dateRange'),
    key: 'dateRange',
    sortable: false,
  },
  {
    title: t('campaign.wrapupLimits'),
    key: 'wrapupLimits',
    sortable: false,
  },
  {
    title: t('campaign.template'),
    key: 'template',
    sortable: false,
  },
  {
    title: t('campaign.relations'),
    key: 'relations',
    sortable: false,
  },
  {
    title: t('campaign.campaignStatus'),
    key: 'status',
    sortable: true,
  },
  {
    title: t('common.isActive'),
    key: 'isActive',
    sortable: true,
  },
  {
    title: t('common.createdAt'),
    key: 'createdAt',
    sortable: true,
  },
  {
    title: t('common.actions'),
    key: 'actions',
    sortable: false,
  },
])

// 👉 Methods
// Load data on mount
onMounted(() => {
  loadCampaigns()
  loadWrapupTemplates()
  loadFieldMapping()

  // Start campaign tour
  startTour({
    tourName: 'campaign',
    steps: campaignTourSteps,
  })
})

const goToCreatePage = () => {
  router.push('/campaign/create')
}

const openEditDialog = (campaign: any) => {
  currentCampaign.value = campaign
  showDialog.value = true
}

const closeDialog = () => {
  showDialog.value = false

  // Reset current campaign when dialog closes
  currentCampaign.value = null
}

// Watch for dialog close to reset current campaign
watch(() => showDialog.value, newValue => {
  if (!newValue) {
    // Reset current campaign when dialog closes
    currentCampaign.value = null
  }
})

// Handle save from dialog component
const handleSaveCampaign = async (data: any) => {
  saving.value = true
  try {
    await $api(`/campaigns/${currentCampaign.value?.id}`, {
      method: 'PATCH',
      body: data,
    })
    showToast({
      type: 'success',
      message: t('campaign.campaignUpdatedSuccessfully'),
    })

    await loadCampaigns()
    closeDialog()
  }
  catch (error) {
    console.error('Error saving campaign:', error)
    showToast({
      type: 'error',
      message: t('campaign.errorSavingCampaign'),
    })
  }
  finally {
    saving.value = false
  }
}

const toggleCampaignStatus = (campaign: any) => {
  confirmDialog.value = {
    title: campaign.isActive ? t('campaign.deactivateCampaign') : t('campaign.activateCampaign'),
    message: campaign.isActive ? t('campaign.deactivateCampaignMessage') : t('campaign.activateCampaignMessage'),
    confirmText: campaign.isActive ? t('common.deactivate') : t('common.activate'),
    cancelText: t('common.cancel'),
    action: async () => {
      try {
        await $api(`/campaigns/${campaign.id}/toggle-active`, { method: 'PATCH' })
        showToast({
          type: 'success',
          message: campaign.isActive ? t('campaign.campaignDeactivatedSuccessfully') : t('campaign.campaignActivatedSuccessfully'),
        })
        await loadCampaigns()
      }
      catch (error) {
        console.error('Error toggling campaign status:', error)
        showToast({
          type: 'error',
          message: t('campaign.errorTogglingCampaignStatus'),
        })
      }
    },
  }
  showConfirmDialog.value = true
}

const getStatusColor = (status: string) => {
  const statusColors: Record<string, string> = {
    draft: 'grey',
    active: 'success',
    paused: 'warning',
    completed: 'info',
    cancelled: 'error',
  }

  return statusColors[status] || 'grey'
}

const getStatusText = (status: string) => {
  const statusTexts: Record<string, string> = {
    draft: t('campaign.statusDraft'),
    active: t('campaign.statusActive'),
    paused: t('campaign.statusPaused'),
    completed: t('campaign.statusCompleted'),
    cancelled: t('campaign.statusCancelled'),
  }

  return statusTexts[status] || status
}

const openDisplayFieldsDialog = (campaign: any) => {
  currentCampaign.value = campaign

  // Parse displayFields if it's a JSON string
  let displayFields = campaign.displayFields
  if (typeof displayFields === 'string') {
    try {
      displayFields = JSON.parse(displayFields)
    }
    catch (error) {
      console.error('Failed to parse displayFields:', error)
      displayFields = { dataTable: [], callInfo: [], callLog: [] }
    }
  }

  displayFieldsConfig.value = {
    dataTable: displayFields?.dataTable || [],
    callInfo: displayFields?.callInfo || [],
    callLog: displayFields?.callLog || [],
  }
  showDisplayFieldsDialog.value = true
}

const closeDisplayFieldsDialog = () => {
  showDisplayFieldsDialog.value = false
  currentCampaign.value = null
  displayFieldsConfig.value = {
    dataTable: [],
    callInfo: [],
    callLog: [],
  }
}

const saveDisplayFields = async () => {
  savingDisplayFields.value = true
  try {
    await $api(`/campaigns/${currentCampaign.value?.id}/display-fields`, {
      method: 'PATCH',
      body: {
        displayFields: displayFieldsConfig.value,
      },
    })
    showToast({
      type: 'success',
      message: t('campaign.displayFieldsUpdatedSuccessfully'),
    })
    await loadCampaigns()
    closeDisplayFieldsDialog()
  }
  catch (error) {
    console.error('Error saving display fields:', error)
    showToast({
      type: 'error',
      message: t('campaign.errorSavingDisplayFields'),
    })
  }
  finally {
    savingDisplayFields.value = false
  }
}
</script>

<template>
  <div>
    <!-- 👉 Page Header -->
    <div class="mb-6">
      <h1 class="text-h4 font-weight-bold mb-2">
        {{ t('campaign.campaignManagement') }}
      </h1>
      <p class="text-body-1 text-medium-emphasis">
        {{ t('campaign.campaignManagementDescription') }}
      </p>
    </div>

    <!-- 👉 Filters -->
    <VCard
      class="mb-6"
      data-tour="campaign-filter"
    >
      <VCardItem class="pb-4">
        <VCardTitle>{{ t('common.filters') }}</VCardTitle>
      </VCardItem>

      <VCardText>
        <VRow>
          <!-- 👉 Select Status -->
          <VCol
            cols="12"
            sm="4"
          >
            <AppSelect
              v-model="selectedStatus"
              :placeholder="t('campaign.selectStatus')"
              :items="statusFilterOptions"
              clearable
              clear-icon="tabler-x"
            />
          </VCol>
          <!-- 👉 Select IsActive -->
          <VCol
            cols="12"
            sm="4"
          >
            <AppSelect
              v-model="selectedIsActive"
              :placeholder="t('common.selectStatus')"
              :items="isActiveOptions"
              clearable
              clear-icon="tabler-x"
            />
          </VCol>
        </VRow>
      </VCardText>

      <VDivider />

      <VCardText class="d-flex flex-wrap gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
              { value: -1, title: t('common.all') },
            ]"
            style="inline-size: 6.25rem;"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div
            style="inline-size: 15.625rem;"
            data-tour="campaign-search"
          >
            <AppTextField
              v-model="searchQuery"
              :placeholder="t('campaign.searchCampaigns')"
            />
          </div>

          <!-- 👉 Add campaign button -->
          <VBtn
            prepend-icon="tabler-plus"
            data-tour="create-campaign-button"
            @click="goToCreatePage"
          >
            {{ t('campaign.createCampaign') }}
          </VBtn>
        </div>
      </VCardText>

      <VDivider />
    </VCard>

    <!-- 👉 Campaign List -->
    <VCard data-tour="campaign-list">
      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :headers="headers"
        :items="campaigns"
        :items-length="totalItems"
        :loading="loading"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <!-- 👉 Campaign Name -->
        <template #item.name="{ item }">
          <div class="d-flex align-center">
            <div>
              <h6 class="text-base font-weight-semibold">
                {{ (item as any).name }}
              </h6>
              <span class="text-sm text-medium-emphasis">
                {{ (item as any).detail || t('common.noDescription') }}
              </span>
            </div>
          </div>
        </template>

        <!-- 👉 Date Range -->
        <template #item.dateRange="{ item }">
          <div class="text-sm">
            <div v-if="(item as any).startDate">
              <VIcon
                icon="tabler-calendar"
                size="16"
                class="me-1"
              />
              {{ formatDate24h((item as any).startDate) }}
            </div>
            <div v-if="(item as any).endDate">
              <VIcon
                icon="tabler-calendar-off"
                size="16"
                class="me-1"
              />
              {{ formatDate24h((item as any).endDate) }}
            </div>
            <div v-if="!(item as any).startDate && !(item as any).endDate">
              <span class="text-medium-emphasis">{{ t('common.notSet') }}</span>
            </div>
          </div>
        </template>

        <!-- 👉 Wrapup Limits -->
        <template #item.wrapupLimits="{ item }">
          <div class="text-sm">
            <div v-if="(item as any).maxTotalWrapup">
              <VIcon
                icon="tabler-target"
                size="16"
                class="me-1"
              />
              {{ t('campaign.maxTotal') }}: {{ (item as any).maxTotalWrapup }}
            </div>
            <div v-if="(item as any).maxWrapupPerGroup">
              <VIcon
                icon="tabler-users"
                size="16"
                class="me-1"
              />
              {{ t('campaign.maxPerGroup') }}: {{ Object.keys((item as any).maxWrapupPerGroup || {}).length }}
            </div>
            <div v-if="!(item as any).maxTotalWrapup && !(item as any).maxWrapupPerGroup">
              <span class="text-medium-emphasis">{{ t('common.notSet') }}</span>
            </div>
          </div>
        </template>

        <!-- 👉 Template -->
        <template #item.template="{ item }">
          <VChip
            v-if="(item as any).wrapupTemplate"
            color="primary"
            size="small"
          >
            {{ (item as any).wrapupTemplate.name }}
          </VChip>
          <span
            v-else
            class="text-medium-emphasis"
          >{{ t('common.notSet') }}</span>
        </template>

        <!-- 👉 Relations Count -->
        <template #item.relations="{ item }">
          <div class="text-sm">
            <div>
              <VIcon
                icon="tabler-list"
                size="16"
                class="me-1"
              />
              {{ (item as any).campaignCallLists?.length || 0 }} {{ t('campaign.callLists') }}
            </div>
            <div>
              <VIcon
                icon="tabler-apps"
                size="16"
                class="me-1"
              />
              {{ (item as any).campaignExternalApps?.length || 0 }} {{ t('campaign.externalApps') }}
            </div>
            <div>
              <VIcon
                icon="tabler-users"
                size="16"
                class="me-1"
              />
              {{ (item as any).campaignTeams?.length || 0 }} {{ t('campaign.teams') }}
            </div>
            <div>
              <VIcon
                icon="tabler-ban"
                size="16"
                class="me-1"
              />
              {{ (item as any).campaignDncGroups?.length || 0 }} {{ t('campaign.dncGroups') }}
            </div>
          </div>
        </template>

        <!-- 👉 Status -->
        <template #item.status="{ item }">
          <VChip
            :color="getStatusColor((item as any).status)"
            size="small"
          >
            {{ getStatusText((item as any).status) }}
          </VChip>
        </template>

        <!-- 👉 Active Status -->
        <template #item.isActive="{ item }">
          <VChip
            :color="(item as any).isActive ? 'success' : 'error'"
            size="small"
          >
            {{ (item as any).isActive ? t('common.active') : t('common.inactive') }}
          </VChip>
        </template>

        <!-- 👉 Created At -->
        <template #item.createdAt="{ item }">
          <span class="text-sm">
            {{ formatDate24h((item as any).createdAt) }}
          </span>
        </template>

        <!-- 👉 Actions -->
        <template #item.actions="{ item }">
          <div class="d-flex gap-2">
            <VBtn
              icon
              variant="text"
              size="small"
              @click="openEditDialog(item as any)"
            >
              <VIcon icon="tabler-edit" />
            </VBtn>
            <VBtn
              icon
              variant="text"
              size="small"
              @click="openDisplayFieldsDialog(item as any)"
            >
              <VIcon icon="tabler-settings" />
            </VBtn>
            <VBtn
              icon
              variant="text"
              size="small"
              :color="(item as any).isActive ? 'error' : 'success'"
              @click="toggleCampaignStatus(item as any)"
            >
              <VIcon :icon="(item as any).isActive ? 'tabler-eye-off' : 'tabler-eye'" />
            </VBtn>
          </div>
        </template>

        <!-- pagination -->
        <template #bottom>
          <TablePagination
            v-model:page="page"
            :items-per-page="itemsPerPage"
            :total-items="totalItems"
          />
        </template>
      </VDataTableServer>
    </VCard>

    <!-- 👉 Create/Edit Dialog -->
    <CampaignEditDialog
      v-model:show-dialog="showDialog"
      :campaign-data="currentCampaign"
      :saving="saving"
      :wrapup-templates="wrapupTemplates"
      @save="handleSaveCampaign"
    />

    <!-- 👉 Display Fields Dialog -->
    <VDialog
      v-model="showDisplayFieldsDialog"
      max-width="1000"
      persistent
    >
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between">
          <span>{{ t('campaign.configureDisplayFields') }}</span>
          <VBtn
            icon
            variant="text"
            @click="closeDisplayFieldsDialog"
          >
            <VIcon icon="tabler-x" />
          </VBtn>
        </VCardTitle>

        <VCardText>
          <DisplayFieldsConfig
            v-model="displayFieldsConfig"
            :available-fields="availableFields"
          />
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn
            variant="outlined"
            @click="closeDisplayFieldsDialog"
          >
            {{ t('common.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="savingDisplayFields"
            @click="saveDisplayFields"
          >
            {{ t('common.save') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 👉 Confirm Dialog -->
    <ConfirmDialog
      v-model:is-dialog-visible="showConfirmDialog"
      :confirmation-question="confirmDialog.title"
      :confirm-title="confirmDialog.confirmText"
      :confirm-msg="confirmDialog.message"
      :cancel-title="confirmDialog.cancelText"
      :cancel-msg="t('common.cancel')"
      @confirm="confirmDialog.action"
    />
  </div>
</template>
